import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { toast } from "react-toastify";
import { ProgressSpinner } from "primereact/progressspinner";
import { FileUpload } from "primereact/fileupload";
import { Avatar } from "primereact/avatar";

// api's
import { getRoles } from "../../api/roleApi";
import { useAuth } from "../../contexts/AuthContext";

// css
import "../../styles/common.scss";

// components
import StudentDetails from "./StudentDetails";
import TeacherDetails from "./TeacherDetails";
import PersonalDetails from "./PersonalDetails";

const AvatarUploader = ({ src, onSelect }) => (
  <div className="flex flex-column align-items-center mb-3" style={{ gap: 8 }}>
    <Avatar
      image={src || undefined}
      icon={!src ? "pi pi-user" : undefined}
      size="xlarge"
      shape="circle"
      style={{ width: 120, height: 120, fontSize: 28 }}
      className="mb-2"
    />
    <FileUpload
      mode="basic"
      name="profile"
      chooseLabel={src ? "Change Photo" : "Upload Photo"}
      accept="image/*"
      maxFileSize={50 * 1024 * 1024}
      customUpload
      auto={false}
      multiple={false}
      onSelect={onSelect}
      chooseOptions={{ icon: 'pi pi-upload', className: 'p-button-outlined p-button-sm' }}
      aria-label="Upload Profile Image"
    />
  </div>
);

// Roles that Management users can assign
const MANAGEMENT_ALLOWED_ROLES = ['Teacher', 'Staff', 'Parent', 'Student'];

const SingleUser = ({
  user,
  setUser,
  onSubmit,
  isEdit,
  lockedRole,
  loading,
  setLoading,
}) => {
  const { user: currentUser } = useAuth();
  const currentUserRole = currentUser?.roleName || currentUser?.role?.name || '';
  const isManagement = currentUserRole === 'Management';

  const [roles, setRoles] = useState([]);
  const [password, setPassword] = useState("");
  const [profilePreview, setProfilePreview] = useState(null);


  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        let data = await getRoles();
        // Management can only assign Teacher/Staff/Parent/Student roles
        if (isManagement) {
          data = data.filter(r => MANAGEMENT_ALLOWED_ROLES.includes(r.name));
        }
        // Opened from a role-specific tab: only that role is assignable
        if (lockedRole) {
          data = data.filter(r => r.name === lockedRole);
        }
        setRoles(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching roles", error);
        setLoading(false);
      }
    };
    fetchRoles();
  }, [lockedRole]);
    useEffect(() => {
    const selectedRole = roles.find(role => role.id === user.roleId);
    if (selectedRole?.name !== 'Student' && !isEdit) {
      // Only clear student-specific fields when role is not Student AND it's not an edit operation
      // This prevents clearing existing student data when editing a student user
      setUser(prevState => ({
        ...prevState,
        full_name: '',
        grade: '',
        gender: '',
        birth_date: null,
        parent_id: null
      }));
    }
  }, [user.roleId, roles, setUser, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPassword(value);
    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePrimeProfileSelect = (e) => {
    const file = e.files && e.files[0] ? e.files[0] : null;
    setUser((prev) => ({ ...prev, profileFile: file || undefined, hasProfileFile: !!file }));
    if (file) {
      const url = URL.createObjectURL(file);
      setProfilePreview(url);
    } else {
      setProfilePreview(null);
    }
  };
  const handleSaveDraft = () => {
    // Files can't be serialized — drop them from the draft
    const { profileFile, hasProfileFile, profileImage, ...draft } = user;
    try {
      localStorage.setItem("sms:addUserDraft", JSON.stringify(draft));
      toast.success("Draft saved. It will be restored next time you open this form.", { autoClose: 2500 });
    } catch (e) {
      toast.error("Could not save draft.");
    }
  };

  const currentImageSrc = profilePreview || user.profileImage?.fileUrl || user.profileImage?.filePath || null;
  const isStudent = roles.find(role => role.id === user.roleId)?.name === 'Student';
  const isTeacher = roles.find(role => role.id === user.roleId)?.name === 'Teacher';

  return (
    <div className="form-group">
      <div className="p-fluid">
        {/* <h2 className="text-center mb-3 text-[#333]">Personal Information</h2> */}
        {!isStudent && !isTeacher && (
          <AvatarUploader src={currentImageSrc} onSelect={handlePrimeProfileSelect} />
        )}

        <div className="grid text-[#333] justify-content-center gap-8 ">
          {(isStudent || isTeacher) ? (
            <>
              {/* <div className="col-12 md:col-4">
                <AvatarUploader src={currentImageSrc} onSelect={handlePrimeProfileSelect} />
                <div className="grid">
                  <div className="p-field mb-2 col-12 md:col-6">
                    <label htmlFor="name" className="font-semibold ">Name</label>
                    <InputText
                      id="name"
                      name="name"
                      value={user.name || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="p-field mb-2 col-12 md:col-6 font-semibold ">
                    <label htmlFor="email">Email</label>
                    <InputText
                      id="email"
                      name="email"
                      value={user.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="p-field mb-2 col-12 md:col-6 font-semibold">
                    <label htmlFor="password">Password</label>
                    <Password
                      id="password"
                      name="password"
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder={
                        isEdit ? "Leave blank to keep current password" : "Enter password"
                      }
                      toggleMask
                      required={!isEdit}
                      disabled={!isChangingPassword}
                      feedback={false}
                    />
                    {password.length > 1 && isEdit && (
                      <span className="flex align-items-center">
                        <InputSwitch
                          className="mt-2 "
                          style={{ transform: "scale(0.7)" }}
                          value={isChangingPassword}
                          checked={isChangingPassword}
                          onChange={() => {
                            setIsChangingPassword(!isChangingPassword);
                          }}
                        />
                        {isChangingPassword ? (
                          <small className="mt-2 mx-1">Password change is enabled.</small>
                        ) : (
                          <small className="mt-2 mx-1">Change Password </small>
                        )}
                      </span>
                    )}
                  </div>

                  <div className="p-field mb-2 col-12 md:col-6 font-semibold">
                    <label htmlFor="address">Address</label>
                    <InputText
                      id="address"
                      name="address"
                      value={user.address || ""}
                      onChange={handleChange}
                      required
                    />  
                  </div>

                  <div className="p-field col-12">
                    <label htmlFor="roleId">Role</label>
                    <Dropdown
                      id="roleId"
                      name="roleId"
                      value={user.roleId}
                      options={roles}
                      onChange={handleChange}
                      placeholder="Select a Role"
                      optionLabel="name"
                      optionValue="id"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div> */}
              <div className="col-12 md:col-3 ">
              <PersonalDetails
                userDetails={user}
                setUserDetails={setUser}
                roleLabel={isTeacher ? "Teacher" : isStudent ? "Student" : null}
              />
              </div>
              <div className="col-12 md:col-8">
                <div className="card p-3 ">
                  {isStudent && (
                    <StudentDetails user={user} setUser={setUser} />
                  )}
                  {isTeacher && (
                    <TeacherDetails user={user} setUser={setUser} />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="grid col-12">
              <div className="col-12 md:col-6 p-field mb-3">
                <label htmlFor="name" className="font-semibold block mb-1">Name</label>
                <InputText
                  id="name"
                  name="name"
                  value={user.name || ""}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
              </div>
              <div className="col-12 md:col-6 p-field mb-3">
                <label htmlFor="email" className="font-semibold block mb-1">Email</label>
                <InputText
                  id="email"
                  name="email"
                  value={user.email || ""}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
              </div>
              {!isEdit && (
                <div className="col-12 md:col-6 p-field mb-3">
                  <label htmlFor="password" className="font-semibold block mb-1">Password</label>
                  <Password
                    id="password"
                    name="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter password"
                    toggleMask
                    required
                    feedback={false}
                    className="w-full"
                    style={{ display: 'flex' }}
                  />
                </div>
              )}

              <div className="col-12 md:col-6 p-field mb-3">
                <label htmlFor="address" className="font-semibold block mb-1">Address</label>
                <InputText
                  id="address"
                  name="address"
                  value={user.address || ""}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
              </div>

              <div className="col-12 p-field mb-3">
                <label htmlFor="roleId" className="font-semibold block mb-1">Role</label>
                <Dropdown
                  id="roleId"
                  name="roleId"
                  value={user.roleId}
                  options={roles}
                  onChange={handleChange}
                  placeholder="Select a Role"
                  optionLabel="name"
                  optionValue="id"
                  className="w-full"
                  required
                  disabled={loading || !!lockedRole}
                />
              </div>
            </div>
          )}

          <div className="w-full flex justify-content-end align-items-end gap-2">
            {!isEdit && (
              <Button
                label="Save Draft"
                icon="pi pi-save"
                className="p-button-outlined px-4 mt-3"
                onClick={handleSaveDraft}
              />
            )}
            <Button
              label={isEdit ? "Update" : "Add"}
              className={`btn-primary flex justify-content-end align-items-end px-6 mt-3 w-3 ${isEdit ? "w-2" : "w-3"}`} 
              onClick={onSubmit}
              // disabled={!isValid || loading}
              // aria-disabled={!isValid || loading}
            />
          </div>
        </div>
      </div>
      {loading && (
        <div style={spinnerContainerStyle}>
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};

const spinnerContainerStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1000,
};
export default SingleUser;
